import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const TaskPerformanceSection = ({ tasks, familyMembers }) => {
  // Calculate performance metrics
  const performanceMetrics = useMemo(() => {
    const totalTasks = tasks?.length;
    const completedTasks = tasks?.filter(t => t?.completed)?.length;
    const highPriorityTasks = tasks?.filter(t => t?.priority === 'high')?.length;
    const highPriorityCompleted = tasks?.filter(t => t?.priority === 'high' && t?.completed)?.length;
    const overdueTasks = tasks?.filter(t => !t?.completed && new Date(t?.date) < new Date())?.length;

    return {
      totalTasks,
      completedTasks,
      completionRate: totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0,
      highPriorityTasks,
      highPriorityCompleted,
      highPriorityRate: highPriorityTasks > 0 ? Math.round((highPriorityCompleted / highPriorityTasks) * 100) : 0,
      overdueTasks,
      onTimeRate: totalTasks > 0 ? Math.round(((totalTasks - overdueTasks) / totalTasks) * 100) : 0
    };
  }, [tasks]);

  // Top performers
  const topPerformers = useMemo(() => {
    const memberPerformance = familyMembers?.map(member => {
      const memberTasks = tasks?.filter(task => task?.personId === member?.id);
      const completed = memberTasks?.filter(task => task?.completed)?.length;
      const total = memberTasks?.length;
      const rate = total > 0 ? Math.round((completed / total) * 100) : 0;

      return {
        ...member,
        completed,
        total,
        rate
      };
    });

    return memberPerformance
      ?.sort((a, b) => b?.rate - a?.rate)
      ?.slice(0, 3);
  }, [tasks, familyMembers]);

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-5">
      <div className="flex items-center gap-2 mb-5">
        <Icon name="trophy" size={20} className="text-accent" />
        <h2 className="text-lg md:text-xl font-semibold text-foreground">
          Task Performance
        </h2>
      </div>

      {/* Key Metrics */}
      <div className="space-y-4 mb-6">
        <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg p-4 border border-primary/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Overall Completion</span>
            <Icon name="trending-up" size={16} className="text-primary" />
          </div>
          <div className="text-3xl font-bold text-primary mb-2">
            {performanceMetrics?.completionRate}%
          </div>
          <div className="text-xs text-muted-foreground">
            {performanceMetrics?.completedTasks} of {performanceMetrics?.totalTasks} tasks
          </div>
        </div>

        <div className="bg-gradient-to-br from-warning/10 to-warning/5 rounded-lg p-4 border border-warning/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">High Priority</span>
            <Icon name="alert-circle" size={16} className="text-warning" />
          </div>
          <div className="text-3xl font-bold text-warning mb-2">
            {performanceMetrics?.highPriorityRate}%
          </div>
          <div className="text-xs text-muted-foreground">
            {performanceMetrics?.highPriorityCompleted} of {performanceMetrics?.highPriorityTasks} completed
          </div>
        </div>

        <div className="bg-gradient-to-br from-success/10 to-success/5 rounded-lg p-4 border border-success/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">On-Time Rate</span>
            <Icon name="check-circle-2" size={16} className="text-success" />
          </div>
          <div className="text-3xl font-bold text-success mb-2">
            {performanceMetrics?.onTimeRate}%
          </div>
          <div className="text-xs text-muted-foreground">
            {performanceMetrics?.overdueTasks} overdue tasks
          </div>
        </div>
      </div>

      {/* Top Performers */}
      <div className="pt-5 border-t border-border">
        <h3 className="text-base font-medium text-foreground mb-3 flex items-center gap-2">
          <Icon name="star" size={16} className="text-accent" />
          Top Performers
        </h3>
        <div className="space-y-3">
          {topPerformers?.map((member, index) => (
            <div key={member?.id} className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-accent/20 to-accent/10 flex items-center justify-center text-accent font-bold text-sm">
                {index + 1}
              </div>
              <div
                className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm"
                style={{ backgroundColor: member?.color }}
              >
                {member?.initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground truncate">
                  {member?.name}
                </div>
                <div className="text-xs text-muted-foreground">
                  {member?.completed}/{member?.total} tasks
                </div>
              </div>
              <div className="flex-shrink-0 text-lg font-bold" style={{ color: member?.color }}>
                {member?.rate}%
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Overdue Tasks Alert */}
      {performanceMetrics?.overdueTasks > 0 && (
        <div className="mt-5 pt-5 border-t border-border">
          <div className="bg-error/10 border border-error/20 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Icon name="alert-triangle" size={18} className="text-error flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-error mb-1">
                  {performanceMetrics?.overdueTasks} Overdue Tasks
                </div>
                <div className="text-xs text-muted-foreground">
                  Review and reschedule overdue tasks to improve completion rates
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskPerformanceSection;